import { CircularProgress, Popover, Tooltip } from "@mui/material";
import CTeListingApi, { CTeCorrection } from "api/CTeListingApi";
import { formatToBRL, formatToCPFOrCNPJ } from "@utils/formatters";
import CTeFilterComponent from "components/filter/cteFilter";
import Modal from "components/modal/modal";
import Table, { Column, OrderInfo, TableAction } from "components/table/cteTable";
import { CTeFilter } from "model/cte/CTeFilter";
import { cteFilterStatus } from "model/cte/CTeFilterStatus";
import { CTe } from "model/cte/CTe";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useRef } from "react";
import PageFrame from "components/page-frame/pageFrame";
import { getItem, setItem } from '@services/LocalStorageService';
import PDFViewer from "@components/pdf/pdfViewer";
import HistoryDetails from "@components/historyDetails/historyDetails";
import fileDownload from "js-file-download";
import { useAuth } from '@contexts/auth';
import { PERMISSIONS } from "@utils/permissions";

const StyledFrame = styled.div`
    width: 100%;
    height: 100%;

    position: relative;

    display: flex;
    flex-direction: column;

    .centered {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .body {
        width: 100%;
        height: 100%;
        max-height: 100%;

        display: flex;
        flex-direction: column;
        position: relative;

        .customize-columns-toggle {
            height: 32px;
            width: fit-content;

            padding: 0 8px;

            margin-left: auto;

            border: 1px solid var(--grey-light);
            border-radius: 8px;

            display: flex;
            align-items: center;
            justify-content: center;

            user-select: none;
            font-family: 'TitilliumWeb';

            cursor: pointer;

            background-color: var(--white);

            :active {
                background-color: var(--grey-lightest);
            }

            :focus-visible {
                outline: none;
            }

            > span {
                text-transform: uppercase;
                font-size: .7rem;
                font-weight: bold;
                letter-spacing: .3px;
            }
        }
    }

    .correcao-frame {
        display: flex;
        flex-direction: column;

        > span {
            margin-bottom: 12px;
        }

        > textarea {
            min-width: 550px;
            min-height: 70px;
        }
    }

    .cce-frame {
        display: flex;
        flex-direction: column;
        min-width: 680px;

        > span {
            margin-bottom: 12px;
        }

        .cce-row {
            display: flex;
            align-items: flex-end;
            margin-bottom: 8px;

            > div {
                display: flex;
                flex-direction: column;
                margin-right: 8px;

                > small {
                    color: var(--grey-regular);
                    margin-bottom: 2px;
                }
            }

            .cce-remove {
                background-color: transparent;
                border: none;
                cursor: pointer;
                margin-bottom: 6px;
                color: var(--grey-dark);

                :disabled {
                    color: var(--grey-light);
                    cursor: default;
                }
            }
        }

        .cce-add {
            width: fit-content;
            background-color: transparent;
            border: none;
            cursor: pointer;
            color: var(--color-primary);
            padding: 0;
            margin-top: 4px;
        }
    }

    .hist-item-frame {
        height: fit-content;
        display: flex;
        align-items: top;

        .data-text {
            white-space: nowrap;
            margin-right: 16px;
        }

        .status-indicator {
            height: 6px;
            width: 6px;
            border-radius: 50%;
            margin-top: 6px;
            margin-left: 8px;
            margin-right: 8px;
            margin-bottom: 6px;
            border: 3px solid var(--color-success);
        }

        .status-link-bar {
            width: 3px;
            min-height: 100%;
            background-color: var(--color-success);
            border-radius: 6px;
            margin-top: 7px;
            margin-left: 12px;
            margin-right: 12px;
        }
    }

    .div-table-cell {
        display: table-cell;
        vertical-align: top;
        padding-left: 10px;
        padding-bottom: 10px;
        padding-top: 15px;
    }

    .line-separator {
        display: table-cell;
        border-left: none;
        border-top: var(--grey-light);
    }
`;

const ModalFrame = styled.div`
        top: 6px;
        left: -14px;
        width: 100%;
        position: absolute;
        transform: translateY(-96px);
        z-index: 1000;
`;

const PAGE_SIZE = 30;

interface PageAndSort {
    sort: OrderInfo;
    page: number;
}

/* linha editavel da CC-e: itemNumber fica como texto ate o envio */
interface CorrectionRow {
    group: string,
    field: string,
    value: string,
    itemNumber: string
}

const emptyCorrection = (): CorrectionRow => ({ group: '', field: '', value: '', itemNumber: '' });

const getFilterStatus = (statusValue: string) => {
    const auxStatus = cteFilterStatus.filter(s => s.value?.toUpperCase() === statusValue.toUpperCase())[0];
    if (auxStatus) {
        return auxStatus;
    }
}

/**
    * Builds the status layout for each individual CTe item on the list with the apropriate color
    * @param cteItem represents the CTe being render on screen
    * @returns a custom JSX element for a CTe's status
    */
const getStatusLayout = (cteItem: CTe) => {

    if (cteItem.reportStatus) {

        let status = getFilterStatus(cteItem.reportStatus);

        return <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
                borderRadius: '50%',
                width: '6px',
                height: '6px',
                marginRight: '6px',
                backgroundColor: status?.refColor
            }}></div>
            <span>{status?.label}</span>
        </div>;
    }

    return <div>Status Error: {cteItem.status}</div>;
}

const CTeListPage = () => {

    /**
         * @param cteItem CTe to get the taker's name from
         * @returns A custom view including a tooltip for takers names in the list
         */
    const takerNameToolTip = (cteItem: CTe) => <Tooltip title={cteItem.takerName || "N/A"} placement="top">
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{cteItem.takerName}</div>
    </Tooltip>

    const [columns, setColumns] = useState<Column[]>([
        { header: 'CNPJ Emitente', accessor: 'company.cnpj', isActive: true, minWidth: 125, customView: (value: any) => value.issuerCnpj },
        { header: 'ID Transação', accessor: 'transactionId', isActive: false, minWidth: 210 },
        { header: 'Data de emissão', accessor: 'issueDate', isActive: true, minWidth: 130 },
        { header: 'Nº Documento', accessor: 'docNum', isActive: true, minWidth: 80 },
        { header: 'Série', accessor: 'series', isActive: true, minWidth: 35 },
        { header: 'Valor', accessor: 'ctAmount', isActive: true, minWidth: 85 },
        { header: 'Status', accessor: 'reportStatus', orderAccessor: "status", isActive: true, minWidth: 140, customView: getStatusLayout },
        { header: 'Chave de Acesso', accessor: 'accessKey', orderAccessor: "accessKey", isActive: false, minWidth: 340 },
        { header: 'Nome Tomador', accessor: 'takerName', isActive: true, minWidth: 160, customView: takerNameToolTip },
        { header: 'Doc. Tomador', accessor: 'takerTaxId', isActive: true, minWidth: 125 },
        { header: 'UF Início', accessor: 'startUf', isActive: true, minWidth: 45 },
        { header: 'UF Fim', accessor: 'endUf', isActive: true, minWidth: 45 },
    ]);

    const listingApi = new CTeListingApi();

    const initialRender = useRef(true);

    const [hasMore, setHasMore] = useState(true);

    const [filters, setFilters] = useState(new CTeFilter());

    const [data, setData] = useState<CTe[]>([]);

    const [pageAndSort, setPageAndSort] = useState<PageAndSort>({ sort: { columnOrdered: columns[2], direction: "DESC" }, page: 0 })

    const [totalAmountCTes, setTotalAmountCTes] = useState(0);

    const [selectedCTes, setSelectedCTes] = useState<CTe[]>([]);

    const [activeCTe, setActiveCTe] = useState<CTe>();

    const [downloadOptionsOpen, setDownloadOptionsOpen] = useState(false);

    const [visualizarDacteOpen, setVisualizarDacteOpen] = useState(false);

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

    const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);

    const [cteHistoryDialogOpen, setCteHistoryDialogOpen] = useState(false);

    const [corrections, setCorrections] = useState<CorrectionRow[]>([emptyCorrection()]);

    const [loadingCartaCorrecao, setLoadingCartaCorrecao] = useState<boolean>(false);
    const [loadingDownload] = useState<boolean>(false);
    const [loadingCancel, setLoadingCancel] = useState<boolean>(false);
    const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
    const [exportingCTes, setExportingCTes] = useState<boolean>(false);
    const [downloadingXml, setDownloadingXml] = useState<boolean>(false);

    const { authInfo } = useAuth();
    const [pdf, setPdf] = useState<any>();
    const [pdfFilename, setPdfFilename] = useState<any>();

    const justificationRef = useRef(null);

    const cteActiveColumn: string = `cte-columns-active-${authInfo?.username}`

    const validDownloadStatuses: string[] = ['AUTORIZADO', 'CANCELADO', 'AUTORIZADO_EPEC', 'ERRO_ENVIO', 'ERRO_SEFAZ', 'REJEITADO_SEFAZ'];

    const actions = useMemo<TableAction[]>(() => [
        {
            html: <i className="bi bi-download table-action action-positive"></i>,
            callback: (event: Event, cte: CTe) => { return handleDownloadClick(event, cte); },
            disabled: (actionTooltipText: string, statusLabel: string) => disableAction(actionTooltipText, statusLabel),
            tooltipText: 'Baixar arquivos',
            requiredPermissions: [PERMISSIONS.DOOTAX.CTE.GERENCIAR.VISUALIZAR]
        },
        {
            html: <i className="bi bi-clock-history table-action action-positive" />,
            callback: (event: Event, cte: CTe) => handleShowCTeHistory(event, cte),
            disabled: (actionTooltipText: string, statusLabel: string) => disableAction(actionTooltipText, statusLabel),
            tooltipText: 'Visualizar histórico',
            requiredPermissions: [PERMISSIONS.DOOTAX.CTE.GERENCIAR.VISUALIZAR]
        },
        {
            html: <i className="bi bi-x-lg table-action action-negative" />,
            callback: (event: Event, cte: CTe) => handleShowCancelDialog(event, cte),
            disabled: (actionTooltipText: string, statusLabel: string) => disableAction(actionTooltipText, statusLabel),
            tooltipText: 'Cancelar CTe',
            requiredPermissions: [PERMISSIONS.DOOTAX.CTE.GERENCIAR.CANCELAR]
        },
        {
            html: <i className="bi bi-card-text table-action action-positive" />,
            callback: (event: Event, cte: CTe) => handleShowCTeAmendForm(event, cte),
            disabled: (actionTooltipText: string, statusLabel: string) => disableAction(actionTooltipText, statusLabel),
            tooltipText: 'Carta de correção',
            requiredPermissions: [PERMISSIONS.DOOTAX.CTE.GERENCIAR.EMITIR_CARTA_CORRECAO]
        },
    ], []);

    useEffect(() => {
        const savedColumnsConfig = getItem(cteActiveColumn);
        if (savedColumnsConfig) {
            setColumns(columns.map(col => ({ ...col, isActive: savedColumnsConfig.includes(col.accessor) })));
        } else {
            setItem(cteActiveColumn, columns.map(col => col.accessor).join(','));
        }
    }, []);

    const setFiltersAndResetPageNum = (updatedFilter: CTeFilter) => {
        setFilters(updatedFilter);
        setPageAndSort({ ...pageAndSort, page: 0 });
    }

    const executeSearch = ({ concatResults }: { concatResults: boolean }) => {

        const { sort: sortInfo, page: pageIndex } = pageAndSort;
        listingApi.fetchFilteredCTes(filters, pageIndex, PAGE_SIZE, sortInfo.columnOrdered.orderAccessor || sortInfo.columnOrdered.accessor, sortInfo.direction).then(({ content, totalElements, last }) => {
            setTotalAmountCTes(totalElements);
            setHasMore(!last);

            const newData = content.map(cte => ({
                ...cte,
                companyId: (cte as any).company.id,
                issuerCnpj: formatToCPFOrCNPJ((cte as any).company.cnpj),
                takerTaxId: formatToCPFOrCNPJ(cte.takerTaxId || ''),
                ctAmount: formatToBRL(cte.ctAmount || '0'),
                isSelected: false,
                disableSelection: !validDownloadStatuses.includes(cte.status!)
            }));

            setData(concatResults ? [...data, ...newData] : newData);
        });
    }

    const handleFilter = useCallback(() => executeSearch({ concatResults: false }), [filters, pageAndSort]);

    useEffect(() => {

        if (initialRender.current) {
            initialRender.current = false;
        } else {
            executeSearch({ concatResults: pageAndSort.page !== 0 });
        }
    }, [pageAndSort]);

    useEffect(() => {
        selectedCTes.length && setData(data.map(item => {
            return {
                ...item,
                isSelected: (selectedCTes.find(i => (i as any).id === item.id) !== undefined)
            }
        }));
    }, [selectedCTes]);

    /**
     * Checks if an action should be disabled for a specific CTe based on it's status
     * @param tooltipText
     * @param statusLabel
     * @returns true if the action should be enabled for a specific CTe
     */
    const disableAction = (tooltipText: string, statusLabel: string) => {
        const statusLower = (statusLabel || '').toLowerCase();
        switch (tooltipText.toLowerCase()) {
            case "cancelar cte":
                return statusLower !== "autorizado";

            case "baixar arquivos":
                return statusLower !== "autorizado" && statusLower !== "cancelado";

            case "carta de correção":
                return statusLower !== "autorizado";

            default:
                return false;
        }
    }

    const handleExportCTes = () => {
        setExportingCTes(true);

        listingApi.exportCTes(filters)
            .then(() => toast.success('CTe\'s exportados com sucesso!'))
            .finally(() => setExportingCTes(false));
    }

    const handleDownloadClick = (event: Event, cte: CTe) => {
        setActiveCTe(cte);
        setDownloadOptionsOpen(true);
        event.stopPropagation();
    }

    /* imprime por um iframe oculto: no lote o PDF chega depois do clique, e um window.open
       fora do gesto do usuario e bloqueado como pop-up (devolve null) */
    const handlePrint = (pdfBytes: Blob) => {
        const pdfUrl = URL.createObjectURL(pdfBytes);
        const frame = document.createElement('iframe');
        frame.style.display = 'none';
        frame.src = pdfUrl;
        frame.onload = () => {
            frame.contentWindow?.focus();
            frame.contentWindow?.print();
            /* o dialogo de impressao segura o conteudo; remove so depois, com folga */
            setTimeout(() => {
                document.body.removeChild(frame);
                URL.revokeObjectURL(pdfUrl);
            }, 60000);
        };
        document.body.appendChild(frame);
    }

    const handleVisualizarDacteClick = (cte: CTe) => {
        setActiveCTe(cte);

        listingApi.downloadDacte(cte).then(res => {
            if (res) {
                setPdf(res.data);
                setPdfFilename(res.headers.filename);
            } else {
                setPdf(undefined);
                setPdfFilename(undefined);
            }
        });

        setVisualizarDacteOpen(true);
        setDownloadOptionsOpen(false);
    }

    const handleShowCTeHistory = (event: Event, cte: CTe) => {
        if (!cte || !cte.id) {
            toast.error('É necessário selecionar um CTe para visualizar seu histórico.');
            return;
        }

        setCteHistoryDialogOpen(true);
        setLoadingHistory(true);

        listingApi.fetchCTeHistory(cte.id)
            .then(res => {
                setLoadingHistory(false);
                setActiveCTe({ ...cte, history: res.data });
            }).catch(() => setLoadingHistory(false));

        event.stopPropagation();
    }

    const handleShowCancelDialog = (event: Event, cte: CTe) => {
        setActiveCTe(cte);
        cte && setCancelDialogOpen(true);
        event.stopPropagation();
    }

    const handleShowCTeAmendForm = (event: Event, cte: CTe) => {
        setActiveCTe(cte);
        setCorrections([emptyCorrection()]);
        cte && setCorrectionDialogOpen(true);
        event.stopPropagation();
    }

    const updateCorrection = (index: number, field: keyof CorrectionRow, value: string) => {
        setCorrections(corrections.map((row, i) => i === index ? { ...row, [field]: value } : row));
    }

    /**
     * Valida as alteracoes da CC-e com os limites do evCCeCTe_v4.00.xsd (os mesmos do backend)
     * @returns a mensagem do primeiro problema encontrado, ou undefined se estiver tudo certo
     */
    const validateCorrections = (rows: CorrectionRow[]) => {
        for (let i = 0; i < rows.length; i++) {
            const { group, field, value, itemNumber } = rows[i];
            const line = rows.length > 1 ? ` (alteração ${i + 1})` : '';

            if (!group.trim() || group.trim().length > 20) {
                return `O grupo alterado deve ter entre 1 e 20 caracteres${line}.`;
            }
            if (!field.trim() || field.trim().length > 20) {
                return `O campo alterado deve ter entre 1 e 20 caracteres${line}.`;
            }
            if (!value.trim() || value.trim().length > 500) {
                return `O valor alterado deve ter entre 1 e 500 caracteres${line}.`;
            }
            if (itemNumber.trim()) {
                const item = Number(itemNumber);
                if (!Number.isInteger(item) || item < 1 || item > 99) {
                    return `O número do item deve ser um inteiro entre 1 e 99${line}.`;
                }
            }
        }
    }

    /**
     * Sends a "correction letter" for a specified CTe
     * @param cte CTe to be updated
     */
    const handleAmendCTe = (cte: CTe) => {
        const error = validateCorrections(corrections);

        if (error) {
            toast.warning(error);
            return;
        }

        const payload: CTeCorrection[] = corrections.map(row => ({
            group: row.group.trim(),
            field: row.field.trim(),
            value: row.value.trim(),
            itemNumber: row.itemNumber.trim() ? Number(row.itemNumber) : null
        }));

        setLoadingCartaCorrecao(true);

        listingApi.amendCTe(cte, payload)
            .then(() => {
                setLoadingCartaCorrecao(false);
                toast.success("Carta de correção enviada com sucesso!");
                setCorrectionDialogOpen(false);
                handleFilter();
            })
            .catch(() => setLoadingCartaCorrecao(false));
    }

    const handleCancelCTe = (cte: CTe) => {
        const textValue = (justificationRef.current as any).value as string;

        if (!textValue || textValue.length < 15 || textValue.length > 255) {
            toast.warning(`A justificativa deve conter entre 15 e 255 caracteres. Quantidade atual: ${textValue.length}`, { autoClose: 3500 });
            return;
        }

        setLoadingCancel(true);

        listingApi.cancelCTe(cte, textValue)
            .then(() => {
                setLoadingCancel(false);
                toast.success("CTe cancelado com sucesso!");
                setCancelDialogOpen(false);
                handleFilter()
            }).catch(() => setLoadingCancel(false));
    }

    const handleToggleColumn = (column: Column) => {
        const savedColumnsConfig = getItem(cteActiveColumn)?.split(',');

        if (!savedColumnsConfig || !savedColumnsConfig.length) {
            setItem(cteActiveColumn, column.accessor);
        } else {
            if (savedColumnsConfig.includes(column.accessor)) {
                savedColumnsConfig.splice(savedColumnsConfig.indexOf(column.accessor), 1);
            } else {
                savedColumnsConfig.unshift(column.accessor);
            }

            setItem(cteActiveColumn, savedColumnsConfig ? savedColumnsConfig.join(',') : '');
        }
    }

    /**
     * Shows/hides a table column selected by the user
     * @param column selected/unselected column
     */
    const shouldActivateColumn = (column: Column) => {
        setColumns(columns.map(col => {
            if (column.accessor === col.accessor) {
                col.isActive = !column.isActive
                handleToggleColumn(col);
            }
            return { ...col };
        }))
    }

    const [actionsEl, setActionsEl] = useState<HTMLButtonElement | null>(null);

    const handleActionsBtnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setActionsEl(event.currentTarget);
    };

    const handleClose = () => {
        setActionsEl(null);
    };

    const open = Boolean(actionsEl);
    const id = open ? 'simple-popover' : undefined;

    const customizeColumnsBtn = <>
        <button
            className="customize-columns-toggle"
            aria-describedby={id}
            onClick={(event) => handleActionsBtnClick(event)}>
            <span>Personalizar colunas</span>
            <i className="bi bi-caret-down-fill" style={{ fontSize: '.75rem', marginLeft: '8px' }} />
        </button>
        <Popover
            id={id}
            open={open}
            anchorEl={actionsEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}>
            <div className='customize-columns-menu'>
                <div className="menu-header">
                    <span>Selecione as colunas que deseja exibir na lista:</span>
                </div>
                <div className="menu-items-frame">
                    {
                        columns.slice().sort((a, b) => {
                            if (a.header === b.header) return 0;
                            if (a.header > b.header) return 1;
                            if (a.header < b.header) return -1;
                            return 0;
                        }).map((col, index) => {
                            return <div key={index} style={{ width: 'fit-content', display: 'flex', padding: '10px', alignItems: 'center' }}>
                                <input type="checkbox"
                                    checked={col.isActive}
                                    onChange={() => shouldActivateColumn(col)}
                                />
                                <span>{col.header}</span>
                            </div>
                        })
                    }
                </div>
            </div>
        </Popover>
    </>;

    const handleSortByColumn = (col: Column) => {
        const { sort: sortInfo } = pageAndSort;

        setPageAndSort({
            page: 0,
            sort: {
                columnOrdered: col,
                direction: (col.accessor !== sortInfo.columnOrdered.accessor) || sortInfo.direction === "DESC" ? "ASC" : "DESC"
            }
        });
    }

    const handleDownloadXml = (cte: CTe) => {
        if (!cte) {
            toast.error('É necessário selecionar um CTe para realizar o download do XML.');
            return;
        }

        setDownloadingXml(true);

        listingApi.downloadXml(cte).then(res => {
            if (res && res.status === 204) {
                toast.info("Não foi encontrado nenhum xml para este CTe.");
            }
        }).finally(() => setDownloadingXml(false));
    }

    const handleDownloadXmlBatch = (ids: number[]) => {
        if (!ids || !ids.length) {
            toast.warning('É necessário selecionar um CTe para realizar o download do XML.');
            return;
        }

        listingApi.downloadXmlBatch(ids).then(res => {
            if (!res || !res.data || res.data.size === 0 || res.status !== 200) {
                toast.info("Não foi encontrado nenhum XML válido para os CTes selecionados.");
            } else {
                fileDownload(res.data, res.headers.filename)
            }
        });
    }

    const handleDownloadDacteBatch = (ids: number[]) => {
        if (!ids || !ids.length) {
            toast.warning('É necessário selecionar um CTe para realizar o download do DACTE.');
            return;
        }

        listingApi.downloadDacteBatch(ids).then(res => {
            if (!res || !res.data || res.data.size === 0 || res.status !== 200) {
                toast.info("Não foi possível gerar nenhum DACTE para os CTes selecionados.");
            } else {
                fileDownload(res.data, res.headers.filename)
            }
        });
    }

    const handleDownloadDacteMerged = (ids: number[]) => {
        if (!ids || !ids.length) {
            toast.warning('É necessário selecionar um CTe para realizar a impressão do DACTE.');
            return;
        }

        listingApi.downloadDacteMerged(ids).then(res => {
            if (!res || !res.data || res.data.size === 0 || res.status !== 200) {
                toast.info("Não foi possível gerar nenhum DACTE para os CTes selecionados.");
            } else {
                handlePrint(res?.data);
            }
        });
    }

    // ----------------------------
    // RETURN
    // ----------------------------
    return <PageFrame><StyledFrame>
        <section className="body">
            <div style={{ width: '100%', display: "flex", marginBottom: '12px' }}>
                <CTeFilterComponent model={filters} onFiltersChange={(updatedFilter: CTeFilter) => setFiltersAndResetPageNum(updatedFilter)} reset={() => setFiltersAndResetPageNum(new CTeFilter())} />
                <button
                    className="customize-columns-toggle"
                    onClick={() => setPageAndSort({ ...pageAndSort, page: 0 })}
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                    <i className="bi bi-arrow-clockwise" />
                </button>

                <button disabled={exportingCTes} className="icon-button action-button centered" style={{ marginLeft: '20px', width: '188px' }} onClick={() => handleExportCTes()}>
                    {exportingCTes ? (
                        <CircularProgress size={14} style={{ 'color': '#FFFFFF' }} />
                    ) : (
                        <>
                            <i className="bi bi-file-earmark-spreadsheet"></i>
                            <span>Exportar CTes</span>
                        </>
                    )}
                </button>
            </div>
            <div style={{ display: 'flex', marginLeft: '6px', alignItems: 'center' }}>
                <span><b style={{ marginLeft: '4px', marginRight: '4px' }}>{totalAmountCTes}</b> CTes encontrados.</span>
                <div style={{ height: '25px', width: '1px', backgroundColor: 'var(--grey-light)', margin: '0 8px', opacity: '.5' }}></div>
                <span><b style={{ marginLeft: '4px', marginRight: '4px' }}>{selectedCTes.length}</b> CTes selecionados.</span>
                {customizeColumnsBtn}
            </div>
            <Table data={data}
                columns={columns}
                orderInfo={pageAndSort.sort}
                onSortByColumn={(col: Column) => handleSortByColumn(col)}
                actionsConfig={{ actions: actions, minWidth: 110 }}
                disableSelection={false}
                onSelectionChange={(ctes: any) => { setSelectedCTes(ctes.filter((ct: any) => ct.isSelected)) }}
                handleFetchMore={() => setPageAndSort({ ...pageAndSort, page: (pageAndSort.page + 1) })}
                hasMore={hasMore}
                batchActions={[
                    {
                        label: 'Baixar XML',
                        actionCallback: handleDownloadXmlBatch,
                        isDisabled: selectedCTes.length === 0
                    },
                    {
                        label: 'Baixar DACTE',
                        actionCallback: handleDownloadDacteBatch,
                        isDisabled: selectedCTes.length === 0
                    },
                    {
                        label: 'Imprimir DACTE',
                        actionCallback: handleDownloadDacteMerged,
                        isDisabled: selectedCTes.length === 0
                    }
                ]}
            />
        </section>

        {/* DIALOG - DOWNLOAD ARQUIVOS CTE */}
        {
            !!activeCTe && !!downloadOptionsOpen && <ModalFrame>
                <Modal
                    headerTitle="Baixar arquivos"
                    closeOnClickAway={true}
                    handleClose={() => setDownloadOptionsOpen(false)}
                    loading={loadingDownload}
                >
                    <span style={{ width: '50%' }}>Selecione a opção desejada:</span>

                    <div style={{ width: '100%', display: 'flex', marginTop: '14px', justifyContent: 'space-around' }}>
                        <button disabled={downloadingXml} className="icon-button centered" style={{ marginRight: '12px' }}
                            onClick={() => handleDownloadXml(activeCTe)}
                        >
                            {downloadingXml ? (<CircularProgress size={14} style={{ 'color': '#19ea81' }} />) : 'Baixar XML'}
                        </button>
                        <button
                            className="icon-button centered"
                            onClick={() => handleVisualizarDacteClick(activeCTe)}
                        >
                            Visualizar DACTE
                        </button>
                    </div>
                </Modal>
            </ModalFrame>
        }

        {/* DIALOG - VISUALIZAR DACTE */}

        {visualizarDacteOpen && (
            <ModalFrame>
                <Modal
                    headerTitle="Visualizar DACTE"
                    closeOnClickAway={true}
                    handleClose={() => {
                        setVisualizarDacteOpen(false);
                        setPdf(undefined);
                        setPdfFilename(undefined);
                    }}
                    headerActions={[
                        {
                            icon: (<i title="Imprimir" className="bi bi-printer" style={{ fontSize: '1rem' }} />),
                            actionCallback: () => handlePrint(pdf)
                        },
                        {
                            icon: (<i title="Download" className="bi bi-download" style={{ fontSize: '1rem' }} />),
                            actionCallback: () => fileDownload(pdf, pdfFilename)
                        }
                    ]}
                    loading={false}>
                    <PDFViewer pdf={pdf} />
                </Modal>
            </ModalFrame>
        )}

        {/* DIALOG - CANCELAR CTE */}
        {
            !!activeCTe && !!cancelDialogOpen && <ModalFrame>
                <Modal
                    headerTitle="Cancelar CTe"
                    closeOnClickAway={true}
                    handleClose={() => setCancelDialogOpen(false)}
                    loading={loadingCancel}
                    actionButtons={[{
                        text: 'Cancelar',
                        actionCallback: () => setCancelDialogOpen(false),
                        positiveAction: false
                    },
                    {
                        text: 'Confirmar',
                        actionCallback: () => handleCancelCTe(activeCTe),
                        positiveAction: true
                    }]}
                >
                    <div className="correcao-frame" key={"cancel"}>
                        <span>Digite a justificativa para o cancelamento do CTe <b>{activeCTe && activeCTe.docNum}</b> :</span>
                        <textarea ref={justificationRef} placeholder="(Texto deve conter entre 15 e 255 caracteres)" />
                    </div>
                </Modal>
            </ModalFrame>
        }

        {/* DIALOG - CARTA DE CORREÇÃO (no CT-e cada alteração aponta grupo e campo do XML) */}
        {
            !!activeCTe && !!correctionDialogOpen && <ModalFrame>
                <Modal
                    headerTitle="Criar carta de correção"
                    closeOnClickAway={false}
                    handleClose={() => setCorrectionDialogOpen(false)}
                    loading={loadingCartaCorrecao}
                    actionButtons={[{
                        text: 'Cancelar',
                        actionCallback: () => setCorrectionDialogOpen(false),
                        positiveAction: false
                    },
                    {
                        text: 'Enviar carta',
                        actionCallback: () => handleAmendCTe(activeCTe),
                        positiveAction: true
                    }]}
                >
                    <div className="cce-frame">
                        <span>Informe as alterações da carta de correção para o CTe: <b>{activeCTe && activeCTe.docNum}</b></span>
                        {
                            corrections.map((row, index) => <div className="cce-row" key={index}>
                                <div>
                                    <small>Grupo</small>
                                    <input type="text" style={{ width: '90px' }} maxLength={20} placeholder="ex.: compl"
                                        value={row.group} onChange={(event) => updateCorrection(index, 'group', event.target.value)} />
                                </div>
                                <div>
                                    <small>Campo</small>
                                    <input type="text" style={{ width: '90px' }} maxLength={20} placeholder="ex.: xObs"
                                        value={row.field} onChange={(event) => updateCorrection(index, 'field', event.target.value)} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <small>Valor</small>
                                    <input type="text" maxLength={500}
                                        value={row.value} onChange={(event) => updateCorrection(index, 'value', event.target.value)} />
                                </div>
                                <div>
                                    <Tooltip title="Só quando o campo está numa lista; o primeiro item é 1">
                                        <small>Item</small>
                                    </Tooltip>
                                    <input type="text" style={{ width: '40px' }} maxLength={2}
                                        value={row.itemNumber} onChange={(event) => updateCorrection(index, 'itemNumber', event.target.value.replace(/\D/g, ""))} />
                                </div>
                                <button className="cce-remove" title="Remover alteração" disabled={corrections.length === 1}
                                    onClick={() => setCorrections(corrections.filter((_, i) => i !== index))}>
                                    <i className="bi bi-trash" />
                                </button>
                            </div>)
                        }
                        <button className="cce-add" onClick={() => setCorrections([...corrections, emptyCorrection()])}>
                            <i className="bi bi-plus" /> Adicionar alteração
                        </button>
                    </div>
                </Modal>
            </ModalFrame>
        }

        {/* DIALOG - HISTÓRICO CTE */}
        {
            cteHistoryDialogOpen && <ModalFrame>
                <Modal
                    headerTitle="Histórico do CTe"
                    closeOnClickAway={false}
                    handleClose={() => setCteHistoryDialogOpen(false)}
                    loading={loadingHistory}
                >
                    <div style={{ display: 'table-table', border: `1px solid ${'var(--grey-light)'}`, borderRadius: '10px' }}>
                        <div style={{ display: 'table-row', fontWeight: 'bold', textAlign: "center" }}>
                            <div style={{ width: '150px', display: 'table-cell', padding: '10px' }}>Data</div>
                            <div style={{ display: 'table-cell' }}>Status</div>
                            <div style={{ minWidth: '100px', display: 'table-cell' }}>Usuário</div>
                        </div>
                        <div style={{ display: 'table-row' }}>
                            <hr className="line-separator" />
                            <hr className="line-separator" />
                            <hr className="line-separator" />
                        </div>
                        {
                            !!activeCTe && !!activeCTe.history && activeCTe.history.map((histItem, index) => <div key={index} style={{ width: '100%', display: 'table-row', padding: '5px' }} >
                                <div className="div-table-cell" style={{ width: '150px' }}>
                                    {histItem.createdAt}
                                </div>
                                <div className="div-table-cell">
                                    <div className="hist-item-frame">
                                        <div>
                                            <Tooltip title={histItem.status}
                                                placement='top' >
                                                <div className="status-indicator" />
                                            </Tooltip>
                                            {
                                                (!!activeCTe.history && index < activeCTe.history.length - 1) ?
                                                    <p className="status-link-bar" ></p>
                                                    : ''
                                            }
                                        </div>
                                        <div style={{ paddingLeft: '15px' }}>
                                            <span style={{
                                                color: `${(index === 0 && histItem.status) ? 'var(--color-success)' : ''}`,
                                                fontSize: '100%',
                                                fontWeight: `${(index === 0 && histItem.status) ? 'bold' : ''}`
                                            }}>{histItem.message}</span>
                                            {histItem.details && !!histItem.details.length && (<HistoryDetails items={histItem.details} />)}
                                        </div>
                                    </div>
                                </div>
                                <div className="div-table-cell" style={{ paddingRight: '10px', textAlign: 'center' }}>
                                    {histItem.username}
                                </div>
                            </div>)
                        }
                    </div>
                </Modal>
            </ModalFrame>
        }

    </StyledFrame ></PageFrame >;
}

export default CTeListPage;
